import { useAdminApi } from '@hooks/useAdminApi';
import { useProductBySymbol } from '@hooks/useProductBySymbol';
import { useUserProductBySymbol } from '@hooks/useUserProductBySymbol';
import { Alert } from '@protos/alerts';
import { PricingDAGResponse } from '@protos/pricing';
import {
  PRODUCT_COMPOSITIONS_COLUMNS,
  PRODUCT_CONTRACTS_COLUMNS,
  PRODUCT_EXCHANGE_SYMBOLS_COLUMNS,
  PRODUCT_META_DATA_COLUMNS,
  PRODUCT_RELATED_COLUMNS,
  Product,
  ProductMetaData,
} from '@protos/product';
import { ADMIN_SETTLEMENTS_FOR_PRODUCT_COLUMNS, SettlementPriceWithDate } from '@protos/settlementProducts';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { checkIsInternalUser } from '@utils/permissions';
import { CellClassParams, CellClickedEvent } from 'ag-grid-enterprise';
import { useEffect, useMemo, useState } from 'react';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';

const OnProductCellClickOptions = (navigate: NavigateFunction) => {
  return {
    onCellClicked(event: CellClickedEvent) {
      if (event.column.getColId() === 'symbol') {
        navigate(`/reference/products/${event.value}`);
      }
    },
    cellStyle(params: CellClassParams) {
      if (params.column.getColId() === 'symbol') {
        return { cursor: 'pointer' };
      }
      return null;
    },
  };
};

export const useProductPage = () => {
  const { apiClient } = useAdminApi();
  const { symbol } = useParams();
  const user = useUserContext();
  const isInternalUser = checkIsInternalUser(user);
  const lowerCaseSymbol = symbol?.toLowerCase();
  const navigate = useNavigate();
  const { hasAccess: externalHasAccess, loaded: userProductsLoaded } = useUserProductBySymbol(lowerCaseSymbol!);
  const hasAccess = externalHasAccess || isInternalUser;
  const { product, loading: productLoading } = useProductBySymbol(lowerCaseSymbol!, !!hasAccess);

  const [relatedProductsData, setRelatedProductsData] = useState<Product[]>([]);
  const [settlementsRowData, setSettlementsRowData] = useState<SettlementPriceWithDate[]>([]);
  const [metaData, setMetaData] = useState<ProductMetaData[]>([]);
  const [pricingDagData, setPricingDagData] = useState<PricingDAGResponse[]>([]);
  const [alertsData, setAlertsData] = useState<Alert[]>([]);
  const [isLoadingBlotters, setIsLoadingBlotters] = useState(true);

  const isLoading = useMemo(() => {
    if (!userProductsLoaded) return true;
    if (hasAccess && productLoading) return true;
    return false;
  }, [userProductsLoaded, hasAccess, productLoading, product]);

  const ProductContractsBlotters = useMemo(
    () => [
      {
        title: 'Compositions',
        data: product?.composition ?? [],
        columns: PRODUCT_COMPOSITIONS_COLUMNS,
        colDefOptions: OnProductCellClickOptions(navigate),
      },
      {
        title: 'Contracts',
        data: product?.contracts ?? [],
        columns: PRODUCT_CONTRACTS_COLUMNS,
      },
      ...(isInternalUser
        ? [
            {
              title: 'Settlements',
              data: settlementsRowData,
              columns: ADMIN_SETTLEMENTS_FOR_PRODUCT_COLUMNS,
              colDefOptions: OnProductCellClickOptions(navigate),
            },
          ]
        : []),
    ],
    [product, settlementsRowData, isInternalUser]
  );

  const ProductInfoBlotters = useMemo(
    () => [
      {
        title: 'Exchange Symbols',
        data: product?.exchange_symbols ?? [],
        columns: PRODUCT_EXCHANGE_SYMBOLS_COLUMNS,
        fullWidth: true,
      },
      ...(isInternalUser
        ? [
            {
              title: 'Related Products',
              data: relatedProductsData,
              columns: PRODUCT_RELATED_COLUMNS,
              colDefOptions: OnProductCellClickOptions(navigate),
            },
            {
              title: 'MetaData',
              data: metaData,
              columns: PRODUCT_META_DATA_COLUMNS,
            },
          ]
        : []),
    ],
    [product, relatedProductsData, metaData, isInternalUser]
  );

  useEffect(() => {
    async function fetchSettlements(symbol: string) {
      if (!apiClient || !product?.specification) return;
      const newSettlements = await apiClient?.getSettlementsForProduct(symbol);
      return newSettlements.prices;
    }

    async function fetchAlerts(symbol: string) {
      if (!apiClient) return;
      const alerts = await apiClient?.getAlertsForProduct(symbol);
      return alerts;
    }

    async function fetchRelatedProducts(symbol: string) {
      if (!apiClient) return;
      const relatedProducts = await apiClient?.getRelatedProductsForProduct(symbol);
      return relatedProducts;
    }

    async function fetchPricingDAG(symbol: string) {
      if (!apiClient) return;
      const pricingDAG = await apiClient?.getPricingDAGForProduct(symbol);
      return pricingDAG;
    }

    async function fetchMetaData(symbol: string) {
      if (!apiClient) return;
      const metaData = await apiClient?.getMetaDataForProduct({ product_symbol: symbol });
      return metaData;
    }

    if (product && product.symbol && !!hasAccess) {
      setIsLoadingBlotters(true);
      Promise.allSettled([
        fetchSettlements(product.symbol),
        fetchAlerts(product.symbol),
        fetchRelatedProducts(product.symbol),
        fetchPricingDAG(product.symbol),
        fetchMetaData(product.symbol),
      ])
        .then(data => {
          if (data[0].status === 'fulfilled') setSettlementsRowData(data[0].value ?? []);
          if (data[1].status === 'fulfilled') setAlertsData(data[1].value ?? []);
          if (data[2].status === 'fulfilled') setRelatedProductsData(data[2].value ?? []);
          if (data[3].status === 'fulfilled') setPricingDagData(data[3].value ?? []);
          if (data[4].status === 'fulfilled') setMetaData(data[4].value ?? []);
        })
        .finally(() => setIsLoadingBlotters(false));
    }

    return () => {
      setSettlementsRowData([]);
      setAlertsData([]);
      setRelatedProductsData([]);
      setPricingDagData([]);
      setMetaData([]);
    };
  }, [apiClient, setSettlementsRowData, product, hasAccess]);

  return {
    product,
    symbol,
    hasAccess: !!hasAccess,
    isLoading,
    ProductContractsBlotters,
    ProductInfoBlotters,
    alertsData,
    isLoadingBlotters,
    pricingDagData,
    metaData,
  };
};
